import Products from 'merchandise-lib/constants/Product';
import QuantityNames from 'merchandise-lib/constants/QuantityName';
import Raven from 'raven-js';
import * as BundleTypes from 'self-service-api/constants/BundleTypes';
import { RECURRING } from 'self-service-api/constants/ChargeTypes';
import { CORE_PLATFORM_SEATS, HUBSPOT_CREDITS as HUBSPOT_CREDITS_PRODUCT_CATEGORY } from 'self-service-api/constants/ProductCategories';
import { PRODUCT_HUBSPOT_FREE } from 'self-service-api/constants/ProductIds';
import { ENTERPRISE, FREE, PROFESSIONAL, STARTER, currentOrderedPaidProductLevels, orderedProductLevels } from 'self-service-api/constants/ProductLevels';
import { CMS, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { SOFTWARE } from 'self-service-api/constants/ProductTypes';
import { PRODUCT_VERSION, QUANTITY_PACK } from 'self-service-api/constants/SkuTypes';
import VersionNumbers from 'self-service-api/constants/VersionNumbers';
import { isMarketingContactsSkuV2 } from 'self-service-api/core/utilities/isMarketingContactsSku';
import { getIsUngatedForSeats } from './gatingUtils';
import { getHasSkuInProductInventory, getProductsFromProductInventoryQuantity } from './productInventoryUtils';
import { isCoreSeatsApiName, isProductVersion, isQuantityPackDetails, isValidProductLevel, isValidProductLine } from './typeguards';
export const CoreSeatApiNameByPaidProductLevel = {
  [STARTER]: Products.CORE_SEATS_STARTER,
  [PROFESSIONAL]: Products.CORE_SEATS_PROFESSIONAL,
  [ENTERPRISE]: Products.CORE_SEATS_ENTERPRISE
};
export const getProductDataIsEmpty = state => {
  const {
    productData
  } = state;
  return Object.values(productData).every(val => {
    if (!val || Object.keys(val).length === 0) return true;
    return false;
  });
};
export const getSKU = ({
  productData: {
    skuMap
  }
}, {
  skuId
}) => {
  return skuMap[skuId];
};
export const getProduct = ({
  productData: {
    productMap,
    productLineLevelToProductId,
    apiNameToProductId
  }
}, {
  productId,
  apiName,
  productLine,
  productLevel
}) => {
  let productIdToUse;
  if (productId) productIdToUse = productId;else if (apiName) productIdToUse = apiNameToProductId[apiName];else if (productLine && productLevel) {
    if (productLevel === FREE) {
      productIdToUse = PRODUCT_HUBSPOT_FREE;
    } else {
      productIdToUse = productLineLevelToProductId && productLineLevelToProductId[productLine] && productLineLevelToProductId[productLine][productLevel];
    }
  }
  if (!productIdToUse) return null;
  return productMap[productIdToUse];
};
export const getSkuMap = ({
  productData: {
    skuMap
  }
}) => skuMap;
export const getSku = (state, {
  skuId
}) => {
  return getSkuMap(state)[skuId];
};
export const getProductForSkuId = (state, {
  skuId
}) => {
  const isEmpty = getProductDataIsEmpty(state);
  if (isEmpty) {
    throw new Error('Attempted to call getProductForSkuId before product data is fetched');
  }
  const {
    productData: {
      skuMap,
      productMap
    }
  } = state;
  if (skuId in skuMap) {
    const {
      productId
    } = skuMap[skuId];
    if (productId in productMap) {
      return productMap[productId];
    }
    throw new Error(`productId ${productId} does not exist in productMap.`, {
      cause: {
        skuId,
        productId
      }
    });
  }
  throw new Error(`skuId ${skuId} does not exist in skuMap.`);
};
export const getProductVersion = (state, {
  skuId,
  apiName,
  versionNumber
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  if (skuId) {
    const sku = skuMap[skuId];
    if (!sku) {
      throw new Error(`skuId ${skuId} does not exist in skuMap.`);
    }
    if (isQuantityPackDetails(sku)) {
      const version = skuMap[sku.productVersionSkuId];
      if (isProductVersion(version)) {
        return version;
      }
    } else {
      return sku;
    }
  }
  const allProductVersions = Object.values(skuMap).filter(sku => isProductVersion(sku));
  return allProductVersions.find(sku => {
    return sku.versionNumber === versionNumber && sku.productApiName === apiName;
  });
};
export const getSkuUuidForSkuId = (state, {
  skuId
}) => {
  const sku = getSku(state, {
    skuId
  });
  return sku.skuUuid;
};
export const getCurrentProductVersion = (state, productIdentifier) => {
  const product = getProduct(state, productIdentifier);
  if (!product) return null;
  const {
    currentSkuId
  } = product;
  return getProductVersion(state, {
    skuId: currentSkuId
  });
};
export const getPackagedQuantityBySkuId = (state, {
  skuId,
  unitName
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const currentProductVersion = skuMap[skuId];
  if (currentProductVersion) {
    const {
      packagedQuantities
    } = currentProductVersion;
    const unit = packagedQuantities && packagedQuantities.find(value => {
      return value.name === unitName;
    });
    if (unit && unit.quantity) {
      return unit.quantity;
    }
  }
  return 0;
};
export const getSkuIdIsQuantityPack = (state, {
  skuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    skuType
  } = skuMap[skuId];
  return skuType === QUANTITY_PACK;
};

// util for getting the included quantity for a a specific seat type (core, sales, service)
export const getIncludedSeatQuantityBySkuId = (state, {
  skuId,
  seatUnitName
}) => getPackagedQuantityBySkuId(state, {
  skuId,
  unitName: seatUnitName
});
export const getCoreSeatQuantityBySkuId = (state, {
  skuId
}) => {
  return getIncludedSeatQuantityBySkuId(state, {
    skuId,
    seatUnitName: QuantityNames.CORE_SEAT
  });
};
export const getSkuIdForFreeProduct = state => {
  const product = getCurrentProductVersion(state, {
    productId: PRODUCT_HUBSPOT_FREE
  });
  return product && product.skuId || 0;
};
export const getCurrentSkuIdForProduct = (state, productIdentifier) => {
  const {
    productLevel
  } = productIdentifier;
  if (productLevel === FREE) {
    return getSkuIdForFreeProduct(state);
  }
  const product = getProduct(state, productIdentifier);
  if (!product) return null;
  const currentProductVersion = getCurrentProductVersion(state, {
    productId: product.id
  });
  return currentProductVersion && currentProductVersion.skuId;
};
const CRM_SUITE_PRODUCTS = {
  [STARTER]: [MARKETING, SALES, SERVICE, CMS, OPERATIONS],
  [PROFESSIONAL]: [MARKETING, SALES, SERVICE, CMS, OPERATIONS],
  [ENTERPRISE]: [MARKETING, SALES, SERVICE, CMS, OPERATIONS]
};
const CRM_SUITE_PRODUCT_LEVELS = [STARTER, PROFESSIONAL, ENTERPRISE];
export const TB_SUITE_LEVEL_TO_API_NAME = {
  [STARTER]: Products.CRM_SUITE_STARTER_BUNDLE,
  [PROFESSIONAL]: Products.CRM_SUITE_PROFESSIONAL_BUNDLE,
  [ENTERPRISE]: Products.CRM_SUITE_ENTERPRISE_BUNDLE
};
export const CORE_SEAT_LEVEL_TO_API_NAME = {
  [STARTER]: Products.CORE_SEATS_STARTER,
  [PROFESSIONAL]: Products.CORE_SEATS_PROFESSIONAL,
  [ENTERPRISE]: Products.CORE_SEATS_ENTERPRISE
};
export const getCrmSuiteProductLinesForProductLevel = (__, {
  productLevel
}) => {
  return CRM_SUITE_PRODUCTS[productLevel] || [];
};
export const getTBSuiteProductForProductLevel = (state, {
  suiteProductLevel
}) => {
  const apiName = TB_SUITE_LEVEL_TO_API_NAME[suiteProductLevel];
  return getProduct(state, {
    apiName
  });
};
export const getIsApiNameTBSuiteBundle = apiName => [Products.CRM_SUITE_STARTER_BUNDLE, Products.CRM_SUITE_PROFESSIONAL_BUNDLE, Products.CRM_SUITE_ENTERPRISE_BUNDLE].includes(apiName);
export const getSkuIdIsATBSuiteBundle = (state, {
  skuId
}) => {
  const isQuantityPack = getSkuIdIsQuantityPack(state, {
    skuId
  });
  if (isQuantityPack) return false;
  const product = getProductForSkuId(state, {
    skuId
  });
  return getIsApiNameTBSuiteBundle(product.apiName);
};
export const getCurrentSuiteProductsForProductLevel = (state, {
  productLevel
}) => {
  const tbProduct = getTBSuiteProductForProductLevel(state, {
    suiteProductLevel: productLevel
  });
  if (tbProduct) {
    return [tbProduct];
  }
  const crmSuiteProductLines = getCrmSuiteProductLinesForProductLevel(state, {
    productLevel
  });
  const crmSuiteProducts = crmSuiteProductLines.map(productLine => {
    return getProduct(state, {
      productLine,
      productLevel
    });
  }).filter(product => !!product);
  return crmSuiteProducts;
};
export const getCurrentSuiteSkuIdsForProductLevel = (state, {
  productLevel
}) => {
  const currentSuiteProducts = getCurrentSuiteProductsForProductLevel(state, {
    productLevel
  });
  return currentSuiteProducts.map(product => {
    return getCurrentSkuIdForProduct(state, {
      productId: product.id
    });
  }).filter(skuId => !!skuId);
};
export const getCurrentSuiteApiNamesForProductLevel = (state, {
  productLevel
}) => {
  const currentSuiteProducts = getCurrentSuiteProductsForProductLevel(state, {
    productLevel
  });
  return currentSuiteProducts.map(product => {
    return product.apiName;
  });
};
export const getSkuIdIsHub = (state, {
  skuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    productLine,
    productLevel
  } = getProductForSkuId(state, {
    skuId
  });
  const {
    skuType
  } = skuMap[skuId];
  return !!productLine && !!productLevel && skuType === PRODUCT_VERSION;
};
const getBundledProductListForSkuId = (state, {
  skuId
}) => {
  const sku = getSku(state, {
    skuId
  });
  if (isProductVersion(sku)) {
    return sku.bundledProducts || [];
  }
  return [];
};
export const getBundledProductsForSkuId = (state, {
  skuId
}) => {
  const bundledList = getBundledProductListForSkuId(state, {
    skuId
  });
  return bundledList.map(item => {
    return getProduct(state, {
      productId: item.productId
    });
  });
};
export const getBundledProductVersionsForSkuId = (state, {
  skuId
}) => {
  const bundledList = getBundledProductListForSkuId(state, {
    skuId
  });
  return bundledList.map(item => {
    return getCurrentProductVersion(state, {
      productId: item.productId
    });
  });
};
export const skuIdHasBundledProducts = (state, {
  skuId
}) => {
  const bundledProducts = getBundledProductsForSkuId(state, {
    skuId
  });
  return bundledProducts.length > 0;
};
export const getSkuIdHasBundledHub = (state, {
  skuId
}) => {
  const bundledProductVersions = getBundledProductVersionsForSkuId(state, {
    skuId
  });
  return bundledProductVersions.length > 0 && bundledProductVersions.some(productVersion => productVersion && getSkuIdIsHub(state, {
    skuId: productVersion.skuId
  }));
};
export const getSkuIdIsOrHasHub = (state, {
  skuId
}) => {
  return getSkuIdIsHub(state, {
    skuId
  }) || getSkuIdHasBundledHub(state, {
    skuId
  });
};
export const productHasBundledProducts = (state, {
  productId
}) => {
  const skuId = getCurrentSkuIdForProduct(state, {
    productId
  });
  return skuId ? skuIdHasBundledProducts(state, {
    skuId
  }) : false;
};
export const getProductLevelForSkuId = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  return product.productLevel;
};
export const getProductSkuIdIsBundle = (state, {
  skuId
}) => {
  const productVersion = getProductVersion(state, {
    skuId
  });
  return !!(productVersion !== null && productVersion !== void 0 && productVersion.isBundle);
};
export const getBundledProductLinesForSkuId = (state, {
  skuId
}) => {
  const isBundle = getProductSkuIdIsBundle(state, {
    skuId
  });
  if (!isBundle) {
    return [];
  }
  const productVersion = getProductVersion(state, {
    skuId
  });
  if (!productVersion) {
    return [];
  }
  return productVersion.bundledProducts.map(bundle => bundle.productLine).filter(productLine => isValidProductLine(productLine));
};

// The selector will only return the SuiteProductLevel if all the skuIds are in the same product level
// on PreSeats and SeatsOnly portals. For TB cohorts this will return null even if the TB is selected
export const getCrmSuiteProductLevelForSkuIdsPreSeatsAndSeatsOnly = (state, {
  skuIds
}) => {
  var _CRM_SUITE_PRODUCT_LE;
  const {
    productData: {
      skuMap,
      productMap
    }
  } = state;
  const productLevelToSuiteProductLines = new Map();
  for (const skuId of skuIds) {
    const sku = skuMap[skuId];
    const {
      productLine,
      productLevel
    } = productMap[sku.productId];
    if (isProductVersion(sku) && productLine && isValidProductLevel(productLevel) && productLevel !== 'FREE') {
      var _productLevelToSuiteP;
      if (!productLevelToSuiteProductLines.has(productLevel)) {
        productLevelToSuiteProductLines.set(productLevel, new Set());
      }
      (_productLevelToSuiteP = productLevelToSuiteProductLines.get(productLevel)) === null || _productLevelToSuiteP === void 0 || _productLevelToSuiteP.add(productLine);
    }
  }
  return (_CRM_SUITE_PRODUCT_LE = CRM_SUITE_PRODUCT_LEVELS.find(productLevel => {
    const foundProductLines = productLevelToSuiteProductLines.get(productLevel);
    if (foundProductLines) {
      const productLines = getCrmSuiteProductLinesForProductLevel(state, {
        productLevel
      });
      return productLines.every(productLine => foundProductLines.has(productLine));
    }
    return false;
  })) !== null && _CRM_SUITE_PRODUCT_LE !== void 0 ? _CRM_SUITE_PRODUCT_LE : null;
};
export const getSkuIdIsInTBSuiteForProductLevel = (state, {
  skuId,
  suiteProductLevel
}) => {
  if (!suiteProductLevel) {
    return false;
  }
  const {
    productLevel,
    productLine
  } = getProductForSkuId(state, {
    skuId
  });
  const suiteProduct = getTBSuiteProductForProductLevel(state, {
    suiteProductLevel
  });
  if (!suiteProduct) {
    return false;
  }
  const currentSuiteProductVersion = getCurrentProductVersion(state, {
    productId: suiteProduct.id
  });
  if (!currentSuiteProductVersion) {
    return false;
  }
  const suiteProductLines = getBundledProductLinesForSkuId(state, {
    skuId: currentSuiteProductVersion.skuId
  });
  return productLine && suiteProductLines.includes(productLine) && productLevel === suiteProductLevel;
};

/*
For PreSeats
  - This will return true for Hub SKUs when FrankenSKU is selected
  - This will return true for additional seats & contacts when FrankenSKU is selected
  - This will return false for Hubs and quantity packs if FrankenSKU is not selected
*/
/*
For SeatsOnly
  - This will return true for core seats regardless of whether or not FrankenSKU is selected
  - This will return true for Hub SKUs when FrankenSKU is selected
  - This will return true for additional seats & contacts when FrankenSKU is selected
  - This will return false for Hubs and quantity packs if FrankenSKU is not selected
*/
/*
For TB
  - This will return true for core seats regardless of whether or not TB SKU is selected
  - This will return false for CRM Suite SKUs
  - This will return false for Individual Hub SKUs
  - This will return false for Sales & Service Seats, with and without TB SKUs
*/
export const getSkuIdIsInCrmSuite = (state, {
  skuId,
  suiteProductLevel
}) => {
  const isUngatedForSeats = getIsUngatedForSeats(state);
  const {
    productLevel,
    productLine,
    apiName
  } = getProductForSkuId(state, {
    skuId
  });
  const crmSuiteProductLines = suiteProductLevel ? getCrmSuiteProductLinesForProductLevel(state, {
    productLevel: suiteProductLevel
  }) : [];

  // Core Seats are similar to quantity packs in that they can be purchased
  // separately from suite, but they can also be considered part of suite
  const isCoreSeat = isUngatedForSeats && isCoreSeatsApiName(apiName);
  return productLine && crmSuiteProductLines.includes(productLine) && productLevel === suiteProductLevel || isCoreSeat;
};

// For 5 hub unbundled suite only
export const getSkuIdsContainOnlyCRMSuite = (state, {
  skuIds
}) => {
  const suiteProductLevel = getCrmSuiteProductLevelForSkuIdsPreSeatsAndSeatsOnly(state, {
    skuIds
  });
  if (!suiteProductLevel) return false;
  return skuIds.every(skuId => getSkuIdIsInCrmSuite(state, {
    skuId,
    suiteProductLevel
  }));
};

// For 5 hub unbundled suite only
export const getCrmSuiteProductLevelForProductConfigurations = (state, {
  productConfigurations
}) => getCrmSuiteProductLevelForSkuIdsPreSeatsAndSeatsOnly(state, {
  skuIds: productConfigurations.map(({
    skuId
  }) => skuId)
});
export const getHubProductVersionSkuIds = (state, {
  skuIds
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  return skuIds.filter(skuId => {
    const sku = skuMap[skuId];
    const product = getProductForSkuId(state, {
      skuId
    });
    return sku.skuType === PRODUCT_VERSION && product.productType === SOFTWARE && product.productLine && product.productLevel;
  });
};
export const getHubProductVersionSkuIdsOrDefault = (state, {
  skuIds
}) => {
  const filtered = getHubProductVersionSkuIds(state, {
    skuIds
  });
  return filtered.length ? filtered : skuIds;
};

/**
 * Gets the billingNameKey for a sku (either a ProductVersion or quantityPack)
 * If the sku passed is a ProductVersion, apiName is required
 */
const getProductBillingNameKey = ({
  apiName,
  sku
}) => {
  if (!sku) return null;
  if (isProductVersion(sku)) {
    if (apiName) {
      return apiName;
    }
    console.error('Must pass apiName to get the billingNameKey of a ProductVersion');
    return null;
  }
  if (isQuantityPackDetails(sku)) {
    const quantityPackApiName = sku.quantityPackApiName;
    return quantityPackApiName;
  }
  return null;
};
export const getBillingNameKey = (state, {
  skuId
}) => {
  const sku = getSku(state, {
    skuId
  });
  const product = getProductForSkuId(state, {
    skuId
  });
  const apiName = product === null || product === void 0 ? void 0 : product.apiName;
  return getProductBillingNameKey({
    apiName,
    sku
  });
};
export const getQuantityPackDetailsListForProductVersion = (state, {
  productVersionSkuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  if (!productVersionSkuId) {
    return [];
  }
  const sku = skuMap[productVersionSkuId];
  return isProductVersion(sku) ? sku.quantityPackDetails : [];
};
export const getQuantityPackForProductVersion = (state, {
  productVersionSkuId,
  quantityPackApiName
}) => {
  const quantityPackDetails = getQuantityPackDetailsListForProductVersion(state, {
    productVersionSkuId
  });
  const MARKETING_QUANTITY_PACK_APINAME = ['additional-marketing-contacts', 'additional-legacy-contacts'];
  const quantityPackApiNames = MARKETING_QUANTITY_PACK_APINAME.includes(quantityPackApiName) ? MARKETING_QUANTITY_PACK_APINAME : [quantityPackApiName];
  return quantityPackDetails.find(quantityPack => {
    return quantityPackApiNames.includes(quantityPack.quantityPackApiName);
  });
};
export const getQuantityPackForProductApiName = (state, {
  productApiName,
  quantityPackApiName
}) => {
  const productSkuId = getCurrentSkuIdForProduct(state, {
    apiName: productApiName
  });
  if (!productSkuId) return null;
  return getQuantityPackForProductVersion(state, {
    productVersionSkuId: productSkuId,
    quantityPackApiName
  });
};
export const getProductVersionHasQuantityPack = (state, {
  productVersionSkuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const sku = skuMap[productVersionSkuId];
  return isProductVersion(sku) && !!sku.quantityPackDetails.length;
};
export const getIncludedCoreSeatsQuantityForProductVersion = (state, {
  productVersionSkuId
}) => {
  const {
    productLevel
  } = getProductForSkuId(state, {
    skuId: productVersionSkuId
  });
  return currentOrderedPaidProductLevels.includes(productLevel) ? getCoreSeatQuantityBySkuId(state, {
    skuId: productVersionSkuId
  }) : 0;
};
export const getIncludedCoreSeatsQuantityForSuiteProductLevel = (state, {
  suiteProductLevel
}) => {
  const product = getTBSuiteProductForProductLevel(state, {
    suiteProductLevel
  });
  if (!product) {
    if (suiteProductLevel === STARTER) return 1;
    const suiteSkuIds = getCurrentSuiteSkuIdsForProductLevel(state, {
      productLevel: suiteProductLevel
    });
    return suiteSkuIds.reduce((currentSum, currentValue) => {
      return currentSum + getCoreSeatQuantityBySkuId(state, {
        skuId: currentValue
      });
    }, 0);
  }
  const {
    currentSkuId
  } = product;
  return getCoreSeatQuantityBySkuId(state, {
    skuId: currentSkuId
  });
};
export const highestProductLevel = (...productLevels) => orderedProductLevels[Math.max(0, ...productLevels.map(productLevel => orderedProductLevels.indexOf(productLevel)))];
export const getHighestProductLevelFromSkuIds = (state, {
  skuIds
}) => {
  const productLevels = skuIds.map(skuId => {
    const {
      productLevel
    } = getProductForSkuId(state, {
      skuId
    });
    return productLevel;
  }).filter(productLevel => !!productLevel && isValidProductLevel(productLevel));
  return highestProductLevel(...productLevels);
};
export const getHighestProductLevelSkuIdsByProductLine = (state, {
  skuIds
}) => {
  return skuIds.reduce((agr, skuId) => {
    const {
      productLine: singleSkuProductLine,
      productLevel,
      currentSkuId: productSkuId
    } = getProductForSkuId(state, {
      skuId
    });
    const skuIdToUse = getSkuIdIsQuantityPack(state, {
      skuId
    }) ? productSkuId : skuId;
    const bundledProductLines = getBundledProductLinesForSkuId(state, {
      skuId
    });
    const allPossibleProductLines = isValidProductLine(singleSkuProductLine) ? [singleSkuProductLine, ...bundledProductLines] : bundledProductLines;
    allPossibleProductLines.forEach(productLine => {
      const currentSkuIdForProductLine = agr[productLine];
      if (currentSkuIdForProductLine) {
        const {
          productLevel: productLevelForCurrentSkuId
        } = getProductForSkuId(state, {
          skuId: currentSkuIdForProductLine
        });
        const isHigherProductLevel = isValidProductLevel(productLevel) && isValidProductLevel(productLevelForCurrentSkuId) && orderedProductLevels.indexOf(productLevel) > orderedProductLevels.indexOf(productLevelForCurrentSkuId);
        agr[productLine] = isHigherProductLevel ? skuIdToUse : currentSkuIdForProductLine;
      } else {
        agr[productLine] = skuIdToUse;
      }
    });
    return agr;
  }, {});
};
export const getSkuIdIsAddonOrService = (state, {
  skuId
}) => {
  const {
    productData: {
      skuMap
    }
  } = state;
  const {
    productLine,
    productLevel,
    apiName
  } = getProductForSkuId(state, {
    skuId
  });
  const {
    skuType
  } = skuMap[skuId];
  const coreSeatSku = isCoreSeatsApiName(apiName);
  if (coreSeatSku) return false;
  return !productLine && !productLevel && skuType === PRODUCT_VERSION;
};
export const getLowestPaidProductLevel = (state, {
  productLine
}) => currentOrderedPaidProductLevels.find(productLevel => {
  const product = getProduct(state, {
    productLine,
    productLevel
  });
  return !!product;
});
export const getIsMarketingContactsSku = (state, {
  skuId
}) => {
  return isMarketingContactsSkuV2({
    skuMap: getSkuMap(state),
    skuId
  });
};
export const getPaidProductsLevelsForProductLine = (state, {
  productLine
}) => currentOrderedPaidProductLevels.filter(productLevel => !!getProduct(state, {
  productLine,
  productLevel
}));
export const getPaidProductsForProductLine = (state, {
  productLine
}) => {
  const filteredLevels = currentOrderedPaidProductLevels.filter(productLevel => !!getProduct(state, {
    productLine,
    productLevel
  }));
  if (!filteredLevels.length) return [];
  const products = filteredLevels.map(productLevel => getProduct(state, {
    productLine,
    productLevel
  }));
  return products;
};
export const getIsQuantifiableSeatProOrEntSku = (state, {
  skuId
}) => {
  const {
    productLine,
    productLevel
  } = getProductForSkuId(state, {
    skuId
  });
  return !!(productLine && productLevel && [SALES, SERVICE, 'COMMERCE'].includes(productLine) && [PROFESSIONAL, ENTERPRISE].includes(productLevel));
};
export const getIsCrmSuiteSalesOrServiceSkuProEnt = (state, {
  skuId,
  suiteProductLevel
}) => {
  const isProductLevelProOrEnt = [PROFESSIONAL, ENTERPRISE].includes(suiteProductLevel);
  const {
    productLine
  } = getProductForSkuId(state, {
    skuId
  });
  const isSkuHub = getSkuIdIsHub(state, {
    skuId
  });
  const isSkuInCrmSuite = getSkuIdIsInCrmSuite(state, {
    skuId,
    suiteProductLevel
  });
  const isProductLineSalesOrService = !!productLine && [SALES, SERVICE].includes(productLine);
  return isSkuInCrmSuite && isSkuHub && isProductLineSalesOrService && isProductLevelProOrEnt;
};
export const getIsCoreSeatSku = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  return isCoreSeatsApiName(product.apiName);
};
export const getIsPaidCoreSeatSku = (state, {
  skuId
}) => {
  const {
    apiName
  } = getProductForSkuId(state, {
    skuId
  });
  return getIsCoreSeatSku(state, {
    skuId
  }) && [Products.CORE_SEATS_STARTER, Products.CORE_SEATS_PROFESSIONAL, Products.CORE_SEATS_ENTERPRISE].includes(apiName);
};
export const getCoreSeatSkuForProductLevel = (state, {
  productLevel
}) => {
  const apiName = CORE_SEAT_LEVEL_TO_API_NAME[productLevel];
  const productVersion = getCurrentProductVersion(state, {
    apiName
  });
  return productVersion;
};

// This is a clone of `isQuantityUpgrade` from self-service-api but without the
// extra API calls and Promises
export const isQuantityUpgrade = (state, {
  productConfigurations
}) => {
  if (!productConfigurations || !productConfigurations.length) {
    return false;
  }
  const isQuantityUpgradeMap = productConfigurations.map(({
    skuId
  }) => {
    var _getProductVersion;
    // Add special case for sales starter and sales pro legacy additional seat purchase
    const {
      apiName
    } = getProductForSkuId(state, {
      skuId
    });
    const {
      versionNumber
    } = (_getProductVersion = getProductVersion(state, {
      skuId
    })) !== null && _getProductVersion !== void 0 ? _getProductVersion : {};
    if (apiName === Products.SALES_HUB_STARTER && versionNumber === VersionNumbers[Products.SALES_HUB_STARTER].LEGACY) {
      return getHasSkuInProductInventory(state, {
        skuId
      });
    }
    if (apiName === Products.SALES_HUB_PROFESSIONAL && versionNumber === VersionNumbers[Products.SALES_HUB_PROFESSIONAL].LEGACY_BUSINESS_USER) {
      return getHasSkuInProductInventory(state, {
        skuId
      });
    }

    // Update for Seats -> treat additional core seat purchases as if they're quantity packs
    if (isCoreSeatsApiName(apiName)) {
      return getHasSkuInProductInventory(state, {
        skuId
      });
    }

    // Update for Seats -> treat sales/service at pro/ent as if they're quantity packs
    if (getIsQuantifiableSeatProOrEntSku(state, {
      skuId
    })) {
      return getHasSkuInProductInventory(state, {
        skuId
      });
    }
    return getSkuIdIsQuantityPack(state, {
      skuId
    });
  });
  return isQuantityUpgradeMap.every(upgrade => !!upgrade);
};
export const isQuantifiableHub = (state, {
  skuId
}) => {
  const isHub = getSkuIdIsHub(state, {
    skuId
  });
  const productVersion = getProductVersion(state, {
    skuId
  });
  return isHub && !!(productVersion !== null && productVersion !== void 0 && productVersion.quantifiable);
};
export const isMarketingContactsQuantityPack = (state, {
  skuId
}) => {
  const sku = getSku(state, {
    skuId
  });
  if (isQuantityPackDetails(sku)) {
    const quantityPackApiName = sku === null || sku === void 0 ? void 0 : sku.quantityPackApiName;
    return quantityPackApiName === 'additional-marketing-contacts' || quantityPackApiName === 'additional-legacy-contacts';
  }
  return false;
};

// eventually will want to add Sales/Service seats that are quantifiable hubs
export const isPaidSalesServiceSeat = (state, {
  skuId
}) => {
  const sku = getSku(state, {
    skuId
  });
  const product = getProductForSkuId(state, {
    skuId
  });
  const quantityPackApiName = isQuantityPackDetails(sku) && sku.quantityPackApiName;
  const isSalesServiceOrCommerceQuantityPack = quantityPackApiName === 'additional-sales-seats' || quantityPackApiName === 'additional-service-seats' || quantityPackApiName === 'additional-commerce-seats';
  const parentSkuIsTBSku = Object.values(TB_SUITE_LEVEL_TO_API_NAME).includes(product && product.apiName);
  return parentSkuIsTBSku && isSalesServiceOrCommerceQuantityPack;
};
export const getPackagedQuantitiesForQuantityPack = (state, {
  skuId
}) => {
  const sku = getSku(state, {
    skuId
  });
  if (!isQuantityPackDetails(sku)) {
    return 0;
  }
  return sku.includedWithProductVersion;
};

/**
 * @deprecated - use useHasAnySuiteBundlesInProductDetails hook instead
 */
export const hasAnyTBSkuInProductData = state => {
  if (getProductDataIsEmpty(state)) return false;
  const tbApiNames = CRM_SUITE_PRODUCT_LEVELS.map(productLevel => TB_SUITE_LEVEL_TO_API_NAME[productLevel]);
  return tbApiNames.some(apiName => getProduct(state, {
    apiName
  }));
};
export const hasAnyMarketingPlusSkuInProductData = state => {
  if (getProductDataIsEmpty(state)) return false;
  const {
    productData: {
      apiNameToProductId
    }
  } = state;
  const marketingPlusApiNames = [Products.MARKETING_PLUS_SUITE_BUNDLE_PROFESSIONAL, Products.MARKETING_PLUS_SUITE_BUNDLE_ENTERPRISE];
  return marketingPlusApiNames.some(apiName => apiNameToProductId[apiName]);
};

//TODO - update to include sales seat quantity packs
export const getIsSalesSeatSku = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  const isSalesProductLine = product && product.productLine === 'SALES';
  const productVersion = getProductVersion(state, {
    skuId
  });
  const isQuantifiable = productVersion && productVersion.quantifiable;
  const hasIncludedSalesSeat = !!getIncludedSeatQuantityBySkuId(state, {
    skuId,
    seatUnitName: QuantityNames.SALES_SEAT
  });
  return isSalesProductLine && isQuantifiable && hasIncludedSalesSeat;
};
export const getIsSalesSeatQuantityPackSku = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  const isTBSuiteBundle = getIsApiNameTBSuiteBundle(product.apiName);
  const sku = getSKU(state, {
    skuId
  });
  return isTBSuiteBundle && isQuantityPackDetails(sku) && sku.quantifiable && (sku === null || sku === void 0 ? void 0 : sku.quantityPackApiName) === 'additional-sales-seats';
};

//TODO - update to include sales seat quantity packs
export const getIsServiceSeatSku = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  const isServiceProductLine = product && product.productLine === 'SERVICE';
  const productVersion = getProductVersion(state, {
    skuId
  });
  const isQuantifiable = productVersion && productVersion.quantifiable;
  const hasIncludedServiceSeat = !!getIncludedSeatQuantityBySkuId(state, {
    skuId,
    seatUnitName: QuantityNames.SERVICE_SEAT
  });
  return isServiceProductLine && isQuantifiable && hasIncludedServiceSeat;
};
export const getIsServiceSeatQuantityPackSku = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  const isTBSuiteBundle = getIsApiNameTBSuiteBundle(product.apiName);
  const sku = getSKU(state, {
    skuId
  });
  return isTBSuiteBundle && isQuantityPackDetails(sku) && sku.quantifiable && (sku === null || sku === void 0 ? void 0 : sku.quantityPackApiName) === 'additional-service-seats';
};
export const getIsCommerceSeatSku = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  const isCommerceProductLine = product && product.productLine === 'COMMERCE';
  const productVersion = getProductVersion(state, {
    skuId
  });
  const isQuantifiable = productVersion && productVersion.quantifiable;
  const hasIncludedCommerceSeat = !!getIncludedSeatQuantityBySkuId(state, {
    skuId,
    seatUnitName: QuantityNames.COMMERCE_SEAT
  });
  return isCommerceProductLine && isQuantifiable && hasIncludedCommerceSeat;
};
export const getIsCommerceSeatQuantityPackSku = (state, {
  skuId
}) => {
  const product = getProductForSkuId(state, {
    skuId
  });
  const isTBSuiteBundle = getIsApiNameTBSuiteBundle(product.apiName);
  const sku = getSKU(state, {
    skuId
  });
  return isTBSuiteBundle && isQuantityPackDetails(sku) && sku.quantifiable && (sku === null || sku === void 0 ? void 0 : sku.quantityPackApiName) === 'additional-commerce-seats';
};
export const getIsHubSpotCreditsSku = (state, {
  skuId
}) => {
  const {
    productCategory,
    chargeType
  } = getProductForSkuId(state, {
    skuId
  });
  return (productCategory === null || productCategory === void 0 ? void 0 : productCategory.name) === HUBSPOT_CREDITS_PRODUCT_CATEGORY && chargeType === RECURRING;
};
export const getIsSmartCrmSku = (state, {
  skuId
}) => {
  var _product$productCateg;
  const product = getProductForSkuId(state, {
    skuId
  });
  return (product === null || product === void 0 || (_product$productCateg = product.productCategory) === null || _product$productCateg === void 0 ? void 0 : _product$productCateg.name) === CORE_PLATFORM_SEATS;
};
export const getUnitNameForSkuId = (state, {
  skuId
}) => {
  if (getIsHubSpotCreditsSku(state, {
    skuId
  })) {
    return QuantityNames.HUBSPOT_CREDITS;
  }
  if (getIsCoreSeatSku(state, {
    skuId
  })) {
    return QuantityNames.CORE_SEAT;
  }
  if (getIsSalesSeatSku(state, {
    skuId
  })) {
    return QuantityNames.SALES_SEAT;
  }
  if (getIsServiceSeatSku(state, {
    skuId
  })) {
    return QuantityNames.SERVICE_SEAT;
  }
  return null;
};
export const getSeatUnitNameForSkuId = (state, {
  skuId
}) => {
  const isCoreSeat = getIsCoreSeatSku(state, {
    skuId
  });
  if (isCoreSeat) {
    return QuantityNames.CORE_SEAT;
  }
  const isSalesSeat = getIsSalesSeatSku(state, {
    skuId
  }) || getIsSalesSeatQuantityPackSku(state, {
    skuId
  });
  if (isSalesSeat) {
    return QuantityNames.SALES_SEAT;
  }
  const isServiceSeat = getIsServiceSeatSku(state, {
    skuId
  }) || getIsServiceSeatQuantityPackSku(state, {
    skuId
  });
  if (isServiceSeat) {
    return QuantityNames.SERVICE_SEAT;
  }
  const isCommerceSeat = getIsCommerceSeatSku(state, {
    skuId
  }) || getIsCommerceSeatQuantityPackSku(state, {
    skuId
  });
  if (isCommerceSeat) {
    return QuantityNames.COMMERCE_SEAT;
  }
  Raven.captureMessage('Attempted to get seat unit name with invalid sku ID', {
    extra: {
      skuId
    }
  });
  throw new Error('getSeatUnitNameForSkuId error, the sku is not a seat sku', {
    cause: {
      skuId
    }
  });
};
export const getBundleType = (state, productIdentifier) => {
  const productVersion = getCurrentProductVersion(state, productIdentifier);
  return productVersion && productVersion.bundleType;
};
export const isSeatUnit = unitName => {
  return [QuantityNames.CORE_SEAT, QuantityNames.SALES_SEAT, QuantityNames.SERVICE_SEAT].includes(unitName);
};
export const isBundleTypeConsistent = (state, {
  apiNames
}) => {
  return apiNames.every(apiName => getBundleType(state, {
    apiName
  }) === getBundleType(state, {
    apiName: apiNames[0]
  }));
};
export const isBundleListWithConsistentType = (state, {
  apiNames
}) => {
  if (isBundleTypeConsistent(state, {
    apiNames
  })) {
    return getBundleType(state, {
      apiName: apiNames[0]
    }) !== BundleTypes.NONE;
  }
};
export const getBundleProductsForBundleType = (state, {
  bundleType
}) => {
  if (bundleType === BundleTypes.NONE) return [];
  const {
    productData: {
      productMap
    }
  } = state;
  return Object.values(productMap).filter(product => {
    const productVersion = getProductVersion(state, {
      skuId: product.currentSkuId
    });
    return productVersion && productVersion.bundleType === bundleType;
  });
};
export const getBundleProductForBundleTypeAndProductLevel = (state, {
  bundleType,
  productLevel
}) => {
  const bundleProducts = getBundleProductsForBundleType(state, {
    bundleType
  });
  return bundleProducts.find(product => product.productLevel === productLevel);
};
export const getSkuIdExistsInBundleProducts = (state, {
  skuId,
  bundleSkuId
}) => {
  if (!bundleSkuId || !skuId) return false;
  const bundleProducts = getBundledProductVersionsForSkuId(state, {
    skuId: bundleSkuId
  });
  return bundleProducts.some(product => (product === null || product === void 0 ? void 0 : product.skuId) === skuId);
};
export const getShouldIncludeLineItem = (state, {
  productConfigurations,
  skuId,
  quantity,
  isUngatedForSeats
}) => {
  var _getProductVersion2;
  const sku = getSKU(state, {
    skuId
  });
  const ownedQuantity = getProductsFromProductInventoryQuantity(state, {
    skuId
  });
  const isAddonOrService = getSkuIdIsAddonOrService(state, {
    skuId
  });
  const {
    quantifiable
  } = (_getProductVersion2 = getProductVersion(state, {
    skuId
  })) !== null && _getProductVersion2 !== void 0 ? _getProductVersion2 : {};
  const isAddingZeroUnits = (isAddonOrService && quantifiable || sku.skuType === QUANTITY_PACK) && ownedQuantity === quantity;
  const shouldLumpIntoHubLineItem = isQuantityPackDetails(sku) && productConfigurations.some(({
    skuId: searchSkuId
  }) => searchSkuId === sku.productVersionSkuId) && !isUngatedForSeats;
  return !(isAddingZeroUnits || shouldLumpIntoHubLineItem);
};
export const getShouldIncludeLineItemTB = (state, {
  skuId,
  quantity
}) => {
  var _getProductVersion3;
  const sku = getSKU(state, {
    skuId
  });
  const ownedQuantity = getProductsFromProductInventoryQuantity(state, {
    skuId
  });
  const isAddonOrService = getSkuIdIsAddonOrService(state, {
    skuId
  });
  const {
    quantifiable
  } = (_getProductVersion3 = getProductVersion(state, {
    skuId
  })) !== null && _getProductVersion3 !== void 0 ? _getProductVersion3 : {};
  const isAddingZeroUnits = (isAddonOrService && quantifiable || sku.skuType === QUANTITY_PACK) && ownedQuantity === quantity;
  return !isAddingZeroUnits;
};
export const getShouldIncludeLineItemPreSeats = (state, {
  productConfigurations,
  skuId,
  quantity
}) => {
  var _getProductVersion4;
  const sku = getSKU(state, {
    skuId
  });
  const ownedQuantity = getProductsFromProductInventoryQuantity(state, {
    skuId
  });
  const isAddonOrService = getSkuIdIsAddonOrService(state, {
    skuId
  });
  const {
    quantifiable
  } = (_getProductVersion4 = getProductVersion(state, {
    skuId
  })) !== null && _getProductVersion4 !== void 0 ? _getProductVersion4 : {};
  const isAddingZeroUnits = (isAddonOrService && quantifiable || sku.skuType === QUANTITY_PACK) && ownedQuantity === quantity;
  const shouldLumpIntoHubLineItem = isQuantityPackDetails(sku) && productConfigurations.some(({
    skuId: searchSkuId
  }) => searchSkuId === sku.productVersionSkuId);
  return !(isAddingZeroUnits || shouldLumpIntoHubLineItem);
};
export const getFilteredProductConfigurationsForShoppingCartTBOnly = (state, {
  productConfigurations
}) => {
  const result = {
    paidCoreSeatProductConfigs: [],
    paidSalesServiceSeatProductConfigs: [],
    hubProductConfigs: [],
    nonSuiteProductConfigs: []
  };
  return productConfigurations.reduce((acc, config) => {
    const hasBundledProducts = skuIdHasBundledProducts(state, {
      skuId: config.skuId
    });
    const isPaidCoreSeat = getIsPaidCoreSeatSku(state, {
      skuId: config.skuId
    });
    const isPaidSalesServiceSeatLocal = isPaidSalesServiceSeat(state, {
      skuId: config.skuId
    });
    const isHubSku = getSkuIdIsHub(state, {
      skuId: config.skuId
    });
    const isQuantityPack = getSkuIdIsQuantityPack(state, {
      skuId: config.skuId
    });
    if (isPaidCoreSeat) {
      return Object.assign({}, acc, {
        paidCoreSeatProductConfigs: [...acc.paidCoreSeatProductConfigs, config]
      });
    }
    if (isPaidSalesServiceSeatLocal) {
      acc.paidSalesServiceSeatProductConfigs.push(config);
      return acc;
    }
    if (isHubSku || isQuantityPack || hasBundledProducts) {
      acc.hubProductConfigs.push(config);
      return acc;
    }
    acc.nonSuiteProductConfigs.push(config);
    return acc;
  }, result);
};
export const hasHubSpotCreditsSkuInProductData = state => {
  if (getProductDataIsEmpty(state)) return false;
  const {
    productData: {
      apiNameToProductId
    }
  } = state;
  const apiNames = [Products.HUBSPOT_CREDITS_COMMITTED, Products.HUBSPOT_CREDITS_COMMITTED_OVERAGE];
  return apiNames.some(apiName => {
    return apiNameToProductId === null || apiNameToProductId === void 0 ? void 0 : apiNameToProductId[apiName];
  });
};